import React, { useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Eye } from "lucide-react";
import { useToast } from "@shared/hooks/use-toast";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { PAGE_SIZE } from "@shared/utils/constants";
import { RecipientListFilter } from "../filter/filter";
import { useGetAllRecipientList, useUpdateRecipientList } from "../hooks";
import { Badge } from "@shared/ui/badge";
import { useGetChoices } from "../../shared/hooks";

interface RecipientListData {
  id: string;
  name: string;
  description: string;
  listType: string;
  recipients: number;
  createdBy: string;
  createdOn: string;
  status: string;
  audienceType: string;
}

interface RecipientFilters {
  audienceType: string;
  listType: string;
  status: string;
}

const RecipientList: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectRecipientListId, setRecipientLisstId] = useState("");

  // Read all params from URL
  const searchQuery = decodeURIComponent(searchParams.get("search") || "");
  const audienceType = searchParams.get("audienceType") || "";
  const listType = searchParams.get("listType") || "";
  const status = searchParams.get("status") || "";
  const currentPage = parseInt(searchParams.get("page") || "1");

  const { data: recipientListData } = useGetAllRecipientList(
    currentPage,
    PAGE_SIZE,
    searchQuery,
    audienceType,
    listType,
    status
  );

  const { data: getAudienceType } = useGetChoices("audience_type");
  const { data: getListType } = useGetChoices("list_type");
  const updateRecipientList = useUpdateRecipientList(
    Number(selectRecipientListId)
  );

  const mapChoices = (data: any) =>
    data?.result?.map((item: any) => ({
      value: item.key,
      label: item.value,
    })) || [];

  const audienceTypeList = useMemo(
    () => mapChoices(getAudienceType),
    [getAudienceType]
  );
  const listTypeList = useMemo(() => mapChoices(getListType), [getListType]);

  const statusList = [
    { label: "Active", value: 0 },
    { label: "Inactive", value: 1 },
  ];

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: PAGE_SIZE,
    startItem: 0,
    endItem: 0,
  });

  // Update pagination - same structure as MessageActivityList
  React.useEffect(() => {
    const totalItems = recipientListData?.count || 0;
    const totalPages = Math.ceil(totalItems / PAGE_SIZE);
    const startItem = (currentPage - 1) * PAGE_SIZE + 1;
    const endItem = Math.min(currentPage * PAGE_SIZE, totalItems);

    setPagination({
      currentPage: currentPage,
      totalPages,
      totalItems,
      itemsPerPage: PAGE_SIZE,
      startItem: totalItems > 0 ? startItem : 0,
      endItem: totalItems > 0 ? endItem : 0,
    });
  }, [recipientListData, currentPage]);

  const resetFilters = () => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
    };

    delete newParams.audienceType;
    delete newParams.listType;
    delete newParams.status;
    delete newParams.search;

    setSearchParams(newParams);
  };

  const handleSearch = (searchTerm: string) => {
    const newParams: Record<string, string> = {
      ...Object.fromEntries(searchParams.entries()),
      ...(audienceType && { audienceType }),
      ...(listType && { listType }),
      ...(status && { status }),
    };

    if (searchTerm.trim()) {
      newParams.search = searchTerm.trim();
    } else {
      delete newParams.search;
    }

    newParams.page = "1";
    setSearchParams(newParams);
  };

  const handlePageChange = (page: number) => {
    const newParams: Record<string, string> = {
      ...Object.fromEntries(searchParams.entries()),
      page: page.toString(),
    };
    setSearchParams(newParams);
  };

  const handleActive = (listId: string, activeStatus: boolean) => {
    setRecipientLisstId(listId);
    const payload = {
      is_active: !activeStatus, // Inverts the boolean
    };
    updateRecipientList.mutateAsync(payload);
  };

  const columns: TableColumn<any>[] = [
    {
      key: "name",
      header: "List Name",
      sortable: true,
    },
    {
      key: "audienceTypeDisplay",
      header: "Audience Type",
      sortable: true,
      render: (value: string) => <Badge variant="default">{value}</Badge>,
    },
    {
      key: "totalRecipients",
      header: "Recipients",
      sortable: true,
      render: (value: number) => (
        <span className="font-medium text-gray-700">{value || 0}</span>
      ),
    },
    {
      key: "filterTypeDisplay",
      header: "List Type",
      sortable: true,
      render: (value: number) => <Badge variant="destructive">{value}</Badge>,
    },
    {
      key: "createdDate",
      header: "Created On",
      render: (value, row) => (
        <div>
          <p className="font-medium">{value ?? "NA"}</p>
          <p className="text-xs text-muted-foreground">
            {row.createdUserRemoteName ?? "NA"}
          </p>
        </div>
      ),
    },
    {
      key: "lastModifiedDate",
      header: "Updated On",
      render: (value, row) => (
        <div>
          <p className="font-medium">{value ?? "NA"}</p>
          <p className="text-xs text-muted-foreground">
            {row.lastModifiedUserRemoteName ?? "NA"}
          </p>
        </div>
      ),
    },
    {
      key: "isActive",
      header: "Status",
      sortable: true,
      render: (value: boolean) => {
        return (
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              value
                ? "bg-green-100 text-green-800"
                : "bg-gray-100 text-gray-800"
            }`}
          >
            {value ? "Active" : "Inactive"}
          </span>
        );
      },
    },
  ];

  const actions = [
    {
      label: "view",
      onClick: (row: RecipientListData) => {
        navigate(
          `/communication-hub/recipient-list/view/${row.id}/${row.name}`
        );
      },
      icon: <Eye className="h-4 w-4 mr-2" />,
    },
    {
      label: "Edit",
      onClick: (row: RecipientListData) => {
        navigate(
          `/communication-hub/recipient-list/edit/${row.id}/${row.name}`
        );
      },
      icon: <Eye className="h-4 w-4 mr-2" />,
    },
    {
      label: "Active/Inactive",
      onClick: (row: any) => {
        handleActive(row.id, row.isActive);
      },
      icon: <Eye className="h-4 w-4 mr-2" />,
    },
  ];

  const filterPanel = (
    <RecipientListFilter
      audienceType={audienceType}
      listType={listType}
      status={status}
      audienceTypeList={audienceTypeList}
      listTypeList={listTypeList}
      statusList={statusList}
      onApplyFilters={(filters) => {
        const newParams: any = {
          ...Object.fromEntries(searchParams.entries()),
          ...(filters.audienceType && { audienceType: filters.audienceType }),
          ...(filters.listType && { listType: filters.listType }),
          ...(filters.status && { status: filters.status }),
        };
        setSearchParams(newParams);
      }}
      onResetFilters={resetFilters}
    />
  );

  const hasActiveFilters = audienceType || listType || status;

  return (
    <div className="space-y-6 p-1">
      <GenericTable
        data={recipientListData?.results || []}
        columns={columns}
        rowKey="id"
        selectable={false}
        actions={actions}
        actionsType="dropdown"
        emptyMessage="No recipient lists found matching your criteria"
        pagination={pagination}
        onPageChange={handlePageChange}
        onSearch={handleSearch}
        searchPlaceholder="Search by list name..."
        filterPanel={filterPanel}
        filters={
          hasActiveFilters ? (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                {[!!audienceType, !!listType, !!status].filter(Boolean).length}{" "}
                filter(s) active
              </span>
            </div>
          ) : null
        }
      />
    </div>
  );
};

export default RecipientList;
